import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import Showroom from "lib/components/builders/communities/plans/showroom/Showroom";
import { useFetchNoAuth } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import { Helmet as Head } from "react-helmet-async";

function ConfigurePlanPage() {
  const { builderId, planType, planId, lotId, showroomPlanViewerOnly } = useAppRouterParams();
  const planViewerOnly = showroomPlanViewerOnly === "true";

  // Fetch information to build a public plan
  const lotSuffix = lotId ? `&lotId=${lotId}` : "";
  const fetchDependencies = lotId ? [builderId, planType, planId, lotId] : [builderId, planType, planId];
  const { data, loading } = useFetchNoAuth<ClientHomePlanBaseData>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/public-plans?planType=${planType}&planId=${planId}${lotSuffix}`,
    fetchDependencies,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return (
    <>
      <Head>
        <title>{getPageTitle(`Configure Your ${data?.masterPlan?.name}`)}</title>
        <meta name="robots" content="noindex" />
      </Head>
      {!loading && data && (
        <Showroom
          builder={data.builder}
          location={data.community}
          lot={data.lot}
          masterPlan={data.masterPlan}
          locationPlan={data.communityPlan}
          homePlan={data.homePlan}
          brochureSource={data.brochureSource}
          publicConfig
          planType={planType}
          lotDiscounts={data.lotDiscounts}
          homePlanDiscounts={data.homePlanDiscounts}
          reportingUnitLots={data.reportingUnitLots}
          planViewerOnly={planViewerOnly}
          elevationOverrides={data.elevationOverrides}
        />
      )}
    </>
  );
}
export const Route = createFileRoute("/builders/$builder_id/configure")({
  beforeLoad: ({ context, params, search }) => {
    if (!context.enabledFeatures.showroomUnification) {
      return;
    }
    const { planType, planId, lotId, showroomPlanViewerOnly } = search as {
      planType?: string;
      planId?: string;
      lotId?: string;
      showroomPlanViewerOnly?: string;
    };
    if (!planType || !planId) {
      return;
    }
    throw redirect({
      to: "/showroom",
      search: {
        builderId: params.builder_id,
        planType,
        planId,
        ...(lotId ? { lotId } : {}),
        ...(showroomPlanViewerOnly ? { showroomPlanViewerOnly } : {}),
      },
      replace: true,
    });
  },
  component: ConfigurePlanPage,
  staticData: { isPublicPage: true },
});
